import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Space,
  Form,
  Input,
  Select,
  message,
  Tabs,
  Table,
  Modal,
  DatePicker,
  Tag,
  Divider,
  Alert,
  Spin,
  Row,
  Col,
  Empty,
  Tooltip
} from 'antd';
import {
  DeleteOutlined,
  SaveOutlined,
  EyeOutlined,
  ArrowLeftOutlined,
  UserOutlined,
  HomeOutlined,
  CheckCircleOutlined,
  LinkOutlined,
  DisconnectOutlined,
  SyncOutlined
} from '@ant-design/icons';
import axios from 'axios';
import AuthService from '../utils/auth';
import dayjs from 'dayjs';

const { Option } = Select;

const TourNoticeEdit = () => {
  const { tourId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tour, setTour] = useState(null);
  const [tourNotice, setTourNotice] = useState(null);
  const [flightInfo, setFlightInfo] = useState({
    outbound: [],
    inbound: [],
    baggageTemplateId: null,
    baggageAllowance: '',
    additionalNote: ''
  });

  // 航班批次相关
  const [flightBatches, setFlightBatches] = useState([]);
  const [flightBatchModalVisible, setFlightBatchModalVisible] = useState(false);
  const [selectedFlightBatchId, setSelectedFlightBatchId] = useState(null);
  const [linkedFlightBatchId, setLinkedFlightBatchId] = useState(null); // 当前关联的航班班次ID
  const [linkedFlightBatch, setLinkedFlightBatch] = useState(null); // 当前关联的航班班次数据

  // 酒店库相关
  const [hotels, setHotels] = useState([]);

  // 游客酒店分配相关
  const [tourists, setTourists] = useState([]);
  const [touristHotelsMap, setTouristHotelsMap] = useState({}); // {touristId: [{hotelId, checkInDate, checkOutDate}]}
  const [selectedTouristIds, setSelectedTouristIds] = useState([]);
  const [batchAssignModalVisible, setBatchAssignModalVisible] = useState(false);
  const [batchHotelForm] = Form.useForm();
  const [savingHotels, setSavingHotels] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

  useEffect(() => {
    fetchData();
  }, [tourId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 获取产品通知书
      const noticeResponse = await axios.get(
        `${API_BASE}/tour-notices/tour/${tourId}`,
        { headers: AuthService.getAuthHeaders() }
      );

      if (noticeResponse.data.success) {
        const data = noticeResponse.data.data;
        setTour(data.tour);
        setTourNotice(data);
        setFlightInfo(data.flightInfo || { outbound: [], inbound: [], baggageTemplateId: null, baggageAllowance: '', additionalNote: '' });

        // 加载关联的航班班次信息
        if (data.tour?.flightBatchId) {
          setLinkedFlightBatchId(data.tour.flightBatchId);
        }
        if (data.flightBatch) {
          setLinkedFlightBatch(data.flightBatch);
        }

        // 根据目的地加载航班批次和酒店
        if (data.tour?.destinationId) {
          fetchFlightBatches(data.tour.destinationId, data.tour.departureDate);
          fetchHotels(data.tour.destinationId);
        }
      }

      // 获取游客列表
      await fetchTourists();

    } catch (error) {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取游客列表及其酒店分配
  const fetchTourists = async () => {
    try {
      const response = await axios.get(
        `${API_BASE}/tourists?tourId=${tourId}`,
        { headers: AuthService.getAuthHeaders() }
      );
      if (response.data.success) {
        const touristList = response.data.data || [];
        setTourists(touristList);

        // 获取每个游客的酒店分配
        const hotelsMap = {};
        for (const tourist of touristList) {
          try {
            const hotelResponse = await axios.get(
              `${API_BASE}/tourist-assignments/${tourist.id}/hotels`,
              { headers: AuthService.getAuthHeaders() }
            );
            if (hotelResponse.data.success) {
              hotelsMap[tourist.id] = hotelResponse.data.data || [];
            }
          } catch (e) {
            hotelsMap[tourist.id] = [];
          }
        }
        setTouristHotelsMap(hotelsMap);
      }
    } catch (error) {
      console.error('获取游客列表失败:', error);
    }
  };

  // 获取航班批次列表
  const fetchFlightBatches = async (destinationId, departureDate) => {
    try {
      const response = await axios.get(
        `${API_BASE}/flight-batches?destinationId=${destinationId}&isActive=true`,
        { headers: AuthService.getAuthHeaders() }
      );
      if (response.data.success) {
        const batches = response.data.data;
        const depDate = departureDate ? dayjs(departureDate).format('YYYY-MM-DD') : null;
        const sortedBatches = batches.sort((a, b) => {
          if (a.departureDate === depDate) return -1;
          if (b.departureDate === depDate) return 1;
          return new Date(b.departureDate) - new Date(a.departureDate);
        });
        setFlightBatches(sortedBatches);
      }
    } catch (error) {
      console.error('获取航班批次失败:', error);
    }
  };

  // 获取酒店列表
  const fetchHotels = async (destinationId) => {
    try {
      const response = await axios.get(
        `${API_BASE}/hotels?destinationId=${destinationId}&isActive=true`,
        { headers: AuthService.getAuthHeaders() }
      );
      if (response.data.success) {
        setHotels(response.data.data);
      }
    } catch (error) {
      console.error('获取酒店列表失败:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_BASE}/tour-notices/tour/${tourId}`,
        { flightInfo },
        { headers: AuthService.getAuthHeaders() }
      );
      message.success('保存成功');
    } catch (error) {
      message.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  // 选择航班批次
  const handleSelectFlightBatch = () => {
    setFlightBatchModalVisible(true);
  };

  // 确认选择航班批次（保存关联关系到Tour表）
  const handleConfirmFlightBatch = async () => {
    const selectedBatch = flightBatches.find(b => b.id === selectedFlightBatchId);
    if (selectedBatch) {
      try {
        // 更新产品的航班班次关联
        await axios.put(
          `${API_BASE}/tours/${tourId}`,
          { flightBatchId: selectedBatch.id },
          { headers: AuthService.getAuthHeaders() }
        );

        // 更新本地状态
        setLinkedFlightBatchId(selectedBatch.id);
        setLinkedFlightBatch(selectedBatch);
        setTour(prev => ({ ...prev, flightBatchId: selectedBatch.id }));

        message.success('已关联航班班次，航班信息将自动同步');
      } catch (error) {
        message.error('关联航班班次失败');
      }
    }
    setFlightBatchModalVisible(false);
  };

  // 取消航班班次关联
  const handleUnlinkFlightBatch = async () => {
    try {
      await axios.put(
        `${API_BASE}/tours/${tourId}`,
        { flightBatchId: null },
        { headers: AuthService.getAuthHeaders() }
      );

      setLinkedFlightBatchId(null);
      setLinkedFlightBatch(null);
      setTour(prev => ({ ...prev, flightBatchId: null }));

      message.success('已取消航班班次关联');
    } catch (error) {
      message.error('操作失败');
    }
  };

  // 批量分配酒店
  const handleBatchAssign = () => {
    if (selectedTouristIds.length === 0) {
      message.warning('请先选择游客');
      return;
    }
    batchHotelForm.resetFields();
    setBatchAssignModalVisible(true);
  };

  // 确认批量分配
  const handleConfirmBatchAssign = async () => {
    try {
      const values = await batchHotelForm.validateFields();

      const hotels = [{
        hotelId: values.hotelId,
        checkInDate: values.checkInDate.format('YYYY-MM-DD'),
        checkOutDate: values.checkOutDate.format('YYYY-MM-DD'),
        roomType: values.roomType || null
      }];

      setSavingHotels(true);

      await axios.post(
        `${API_BASE}/tourist-assignments/batch-hotels`,
        { touristIds: selectedTouristIds, hotels },
        { headers: AuthService.getAuthHeaders() }
      );

      message.success(`已为 ${selectedTouristIds.length} 位游客分配酒店`);
      setBatchAssignModalVisible(false);
      setSelectedTouristIds([]);

      // 刷新游客酒店数据
      await fetchTourists();
    } catch (error) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      }
    } finally {
      setSavingHotels(false);
    }
  };

  // 清除单个游客的酒店分配
  const handleClearTouristHotels = async (touristId) => {
    try {
      await axios.put(
        `${API_BASE}/tourist-assignments/${touristId}/hotels`,
        { hotels: [] },
        { headers: AuthService.getAuthHeaders() }
      );
      message.success('已清除酒店分配');
      await fetchTourists();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const flightColumns = [
    { title: '出发地', dataIndex: 'from', width: 90 },
    { title: '目的地', dataIndex: 'to', width: 90 },
    { title: '航班号', dataIndex: 'flight', width: 70 },
    { title: '起飞日期', dataIndex: 'date', width: 80 },
    { title: '起飞时间', dataIndex: 'time', width: 70 },
    { title: '到达日期', dataIndex: 'arrivalDate', width: 80 },
    { title: '到达时间', dataIndex: 'arrivalTime', width: 70 },
    { title: '备注', dataIndex: 'note', width: 100, ellipsis: true }
  ];

  // 游客酒店分配表格列
  const touristHotelColumns = [
    {
      title: '游客',
      key: 'tourist',
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500, marginBottom: 2 }}>
            <UserOutlined style={{ marginRight: 6, color: '#1890ff' }} />
            {record.touristName}
          </div>
          {record.passportName && (
            <div style={{ fontSize: 12, color: '#888' }}>
              {record.passportName}
            </div>
          )}
        </div>
      )
    },
    {
      title: '已分配酒店',
      key: 'hotels',
      render: (_, record) => {
        const hotelsList = touristHotelsMap[record.id] || [];
        if (hotelsList.length === 0) {
          return (
            <span style={{ color: '#bbb' }}>
              <HomeOutlined style={{ marginRight: 6 }} />
              未分配酒店
            </span>
          );
        }
        return (
          <div>
            {hotelsList.map((th, idx) => (
              <div
                key={idx}
                style={{
                  background: '#f0f7ff',
                  borderRadius: 6,
                  padding: '8px 12px',
                  marginBottom: idx < hotelsList.length - 1 ? 8 : 0
                }}
              >
                <div style={{ fontWeight: 500, color: '#1890ff', marginBottom: 4 }}>
                  <HomeOutlined style={{ marginRight: 6 }} />
                  {th.hotel?.name}
                  {th.hotel?.starRating && (
                    <span style={{ color: '#faad14', marginLeft: 6, fontSize: 12 }}>
                      {'★'.repeat(th.hotel.starRating)}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>
                  <span>入住：{th.checkInDate}</span>
                  <span style={{ margin: '0 8px' }}>→</span>
                  <span>退房：{th.checkOutDate}</span>
                </div>
              </div>
            ))}
          </div>
        );
      }
    },
    {
      title: '状态',
      key: 'status',
      width: 100,
      align: 'center',
      render: (_, record) => {
        const hotelsList = touristHotelsMap[record.id] || [];
        return hotelsList.length > 0 ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>已分配</Tag>
        ) : (
          <Tag color="default">待分配</Tag>
        );
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      align: 'center',
      render: (_, record) => {
        const hotelsList = touristHotelsMap[record.id] || [];
        if (hotelsList.length === 0) return null;
        return (
          <Tooltip title="清除酒店分配">
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleClearTouristHotels(record.id)}
            />
          </Tooltip>
        );
      }
    }
  ];

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const hasDestination = !!tour?.destinationId;
  const isDaytour = tour?.tourType === 'daytour';

  // 统计已分配酒店的游客数量
  const assignedCount = tourists.filter(t => (touristHotelsMap[t.id] || []).length > 0).length;

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => {
              // 从sessionStorage读取之前保存的页面状态
              const savedPage = sessionStorage.getItem('tourManagement_page');
              const savedSearch = sessionStorage.getItem('tourManagement_search');
              const savedDate = sessionStorage.getItem('tourManagement_date');

              // 构建URL参数
              const params = new URLSearchParams();
              if (savedPage && savedPage !== '1') params.set('page', savedPage);
              if (savedSearch) params.set('search', savedSearch);
              if (savedDate) params.set('date', savedDate);

              const queryString = params.toString();
              navigate(`/admin/tours${queryString ? '?' + queryString : ''}`);
            }}>
              返回
            </Button>
            <span>编辑出团通知书：{tour?.productName}</span>
            {tour?.destination?.name && <Tag color="purple">{tour.destination.name}</Tag>}
            {isDaytour && <Tag color="cyan">一日游</Tag>}
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<EyeOutlined />}
              onClick={() => window.open(`/admin/tours/${tourId}/notice-preview`, '_blank')}
            >
              预览
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
            >
              保存
            </Button>
          </Space>
        }
      >
        {!hasDestination && (
          <Alert
            message="该产品尚未设置目的地"
            description="请先在产品编辑页面设置目的地，然后才能从航班批次库和酒店库中选择数据。"
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {isDaytour && (
          <Alert
            message="该产品为一日游团型，不含机票和酒店安排"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Tabs
          defaultActiveKey="flight"
          items={[
            ...(!isDaytour ? [{
              key: 'flight',
              label: (
                <span>
                  航班信息
                  {linkedFlightBatchId && (
                    <Tag color="green" icon={<LinkOutlined />} style={{ marginLeft: 8 }}>已关联</Tag>
                  )}
                </span>
              ),
              children: (
                <div>
                  {hasDestination && (
                    <div style={{ marginBottom: 16 }}>
                      {linkedFlightBatchId ? (
                        // 已关联航班班次的情况
                        <Alert
                          message={
                            <Space>
                              <SyncOutlined spin={false} style={{ color: '#52c41a' }} />
                              <span>已关联航班班次 (出发日期: {linkedFlightBatch?.departureDate || '未知'})</span>
                            </Space>
                          }
                          description="航班信息将自动同步。修改航班班次管理中的数据后，通知书将自动更新。"
                          type="success"
                          showIcon={false}
                          action={
                            <Space direction="vertical" size="small">
                              <Button size="small" onClick={handleSelectFlightBatch}>
                                更换班次
                              </Button>
                              <Button size="small" danger onClick={handleUnlinkFlightBatch} icon={<DisconnectOutlined />}>
                                取消关联
                              </Button>
                            </Space>
                          }
                          style={{ marginBottom: 16 }}
                        />
                      ) : (
                        // 未关联航班班次的情况
                        <Alert
                          message="尚未关联航班班次"
                          description="请选择一个航班班次进行关联，关联后航班信息将自动同步更新。"
                          type="warning"
                          showIcon
                          action={
                            <Button type="primary" icon={<LinkOutlined />} onClick={handleSelectFlightBatch}>
                              关联航班班次
                            </Button>
                          }
                          style={{ marginBottom: 16 }}
                        />
                      )}
                    </div>
                  )}

                  {/* 显示实时航班数据（如果已关联则显示关联的班次数据） */}
                  {(() => {
                    const displayFlightInfo = linkedFlightBatch ? {
                      outbound: linkedFlightBatch.flightInfo?.outbound || [],
                      inbound: linkedFlightBatch.flightInfo?.inbound || [],
                      baggageAllowance: linkedFlightBatch.baggageAllowance || '',
                      additionalNote: linkedFlightBatch.additionalNote || ''
                    } : flightInfo;

                    return (
                      <>
                        <h4>去程航班</h4>
                        <Table
                          columns={flightColumns}
                          dataSource={displayFlightInfo.outbound.map((f, i) => ({ ...f, key: i }))}
                          pagination={false}
                          size="small"
                          locale={{ emptyText: '暂无去程航班信息' }}
                        />

                        <Divider />

                        <h4>回程航班</h4>
                        <Table
                          columns={flightColumns}
                          dataSource={displayFlightInfo.inbound.map((f, i) => ({ ...f, key: i }))}
                          pagination={false}
                          size="small"
                          locale={{ emptyText: '暂无回程航班信息' }}
                        />

                        <Divider />

                        <Row gutter={16}>
                          <Col span={8}>
                            <Form.Item label="行李数额（件数）">
                              <Input
                                placeholder="2"
                                value={displayFlightInfo.baggageAllowance}
                                disabled={!!linkedFlightBatchId}
                                onChange={(e) =>
                                  setFlightInfo({ ...flightInfo, baggageAllowance: e.target.value })
                                }
                              />
                              {linkedFlightBatchId && (
                                <span style={{ color: '#888', fontSize: 12 }}>自动同步自航班班次</span>
                              )}
                            </Form.Item>
                          </Col>
                          <Col span={16}>
                            <Form.Item label="额外说明">
                              <Input
                                placeholder="如：11月13日的高铁不含，需要客户自理"
                                value={displayFlightInfo.additionalNote}
                                disabled={!!linkedFlightBatchId}
                                onChange={(e) =>
                                  setFlightInfo({ ...flightInfo, additionalNote: e.target.value })
                                }
                              />
                              {linkedFlightBatchId && (
                                <span style={{ color: '#888', fontSize: 12 }}>自动同步自航班班次</span>
                              )}
                            </Form.Item>
                          </Col>
                        </Row>
                      </>
                    );
                  })()}
                </div>
              )
            }] : []),
            ...(!isDaytour ? [{
              key: 'hotel',
              label: (
                <span>
                  游客酒店分配
                  {tourists.length > 0 && (
                    <Tag color={assignedCount === tourists.length ? 'green' : 'orange'} style={{ marginLeft: 8 }}>
                      {assignedCount}/{tourists.length}
                    </Tag>
                  )}
                </span>
              ),
              children: (
                <div>
                  <Alert
                    message="为每位游客分配专属酒店"
                    description="同一产品的航班信息、注意事项、附件对所有游客相同，但酒店可以不同。选择游客后点击「批量分配酒店」为选中的游客统一分配。"
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />

                  {!hasDestination ? (
                    <Empty description="请先设置产品目的地" />
                  ) : tourists.length === 0 ? (
                    <Empty description="该产品暂无游客，请先添加游客" />
                  ) : (
                    <>
                      <div style={{ marginBottom: 16 }}>
                        <Space>
                          <Button
                            type="primary"
                            icon={<HomeOutlined />}
                            onClick={handleBatchAssign}
                            disabled={selectedTouristIds.length === 0}
                          >
                            批量分配酒店
                          </Button>
                          {selectedTouristIds.length > 0 && (
                            <span style={{ color: '#1890ff' }}>
                              已选择 {selectedTouristIds.length} 位游客
                            </span>
                          )}
                        </Space>
                      </div>

                      <Table
                        columns={touristHotelColumns}
                        dataSource={tourists.map(t => ({ ...t, key: t.id }))}
                        rowSelection={{
                          selectedRowKeys: selectedTouristIds,
                          onChange: setSelectedTouristIds
                        }}
                        pagination={tourists.length > 10 ? { pageSize: 10 } : false}
                        size="middle"
                        style={{ marginTop: 8 }}
                      />
                    </>
                  )}
                </div>
              )
            }] : []),
          ]}
        />
      </Card>

      {/* 航班批次选择模态框 */}
      <Modal
        title="选择航班批次"
        open={flightBatchModalVisible}
        onOk={handleConfirmFlightBatch}
        onCancel={() => setFlightBatchModalVisible(false)}
        okText="确认选择"
        cancelText="取消"
        width={800}
        okButtonProps={{ disabled: !selectedFlightBatchId }}
      >
        {flightBatches.length === 0 ? (
          <Empty description="该目的地暂无航班批次，请先在航班批次管理中添加" />
        ) : (
          <Table
            dataSource={flightBatches}
            rowKey="id"
            size="small"
            pagination={false}
            rowSelection={{
              type: 'radio',
              selectedRowKeys: selectedFlightBatchId ? [selectedFlightBatchId] : [],
              onChange: (keys) => setSelectedFlightBatchId(keys[0])
            }}
            columns={[
              {
                title: '出发日期',
                dataIndex: 'departureDate',
                width: 120,
                render: (date) => {
                  const isMatch = tour?.departureDate && dayjs(date).format('YYYY-MM-DD') === dayjs(tour.departureDate).format('YYYY-MM-DD');
                  return (
                    <span>
                      {date}
                      {isMatch && <Tag color="green" style={{ marginLeft: 4 }}>匹配</Tag>}
                    </span>
                  );
                }
              },
              {
                title: '去程航班',
                dataIndex: ['flightInfo', 'outbound'],
                render: (flights) => flights?.length ? `${flights.length}个航段` : '-'
              },
              {
                title: '回程航班',
                dataIndex: ['flightInfo', 'inbound'],
                render: (flights) => flights?.length ? `${flights.length}个航段` : '-'
              },
              { title: '行李说明', dataIndex: 'baggageNote', ellipsis: true },
              {
                title: '状态',
                dataIndex: 'isActive',
                width: 80,
                render: (active) => <Tag color={active ? 'green' : 'default'}>{active ? '启用' : '停用'}</Tag>
              }
            ]}
            onRow={(record) => ({
              onClick: () => setSelectedFlightBatchId(record.id),
              style: { cursor: 'pointer' }
            })}
          />
        )}
      </Modal>

      {/* 批量分配酒店模态框 */}
      <Modal
        title={`为 ${selectedTouristIds.length} 位游客分配酒店`}
        open={batchAssignModalVisible}
        onOk={handleConfirmBatchAssign}
        onCancel={() => setBatchAssignModalVisible(false)}
        okText="确认分配"
        cancelText="取消"
        confirmLoading={savingHotels}
        width={600}
      >
        <Alert
          message="注意：此操作将覆盖选中游客已有的酒店分配"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form form={batchHotelForm} layout="vertical">
          <Form.Item
            name="hotelId"
            label="选择酒店"
            rules={[{ required: true, message: '请选择酒店' }]}
          >
            <Select placeholder="请选择酒店" showSearch optionFilterProp="children">
              {hotels.map(h => (
                <Option key={h.id} value={h.id}>
                  {h.name} ({h.city}) {'★'.repeat(h.starLevel)}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="checkInDate"
                label="入住日期"
                rules={[{ required: true, message: '请选择入住日期' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="checkOutDate"
                label="退房日期"
                rules={[{ required: true, message: '请选择退房日期' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="roomType" label="房型（可选）">
            <Input placeholder="如：标准双人房" />
          </Form.Item>
        </Form>

        <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 4 }}>
          <p style={{ margin: 0, fontWeight: 'bold', marginBottom: 8 }}>将分配给以下游客：</p>
          <Space wrap>
            {selectedTouristIds.map(id => {
              const tourist = tourists.find(t => t.id === id);
              return tourist ? (
                <Tag key={id} icon={<UserOutlined />}>{tourist.touristName}</Tag>
              ) : null;
            })}
          </Space>
        </div>
      </Modal>
    </div>
  );
};

export default TourNoticeEdit;
